import React from 'react';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import VerticalScrollableContainer from 'common/components/container/VerticalScrollableContainer';
import { Divider, List } from 'common/components/material';
import {
  AboutConnectXMenuItem,
  LogoutMenuItem,
  ManageOrganizationMenuItem,
  ManageOrganizationUsersMenuItem,
  CreateOrganizationMenuItem,
  ManageOrganizationStaticDataMenuItem,
} from 'common/components/menu-items';
import OrganizationLinkMenuItem from 'common/components/menu-items/organization-link-menu-item';
import ManageNotificationsMenuItem from 'common/components/menu-items/ManageNotificationsMenuItem';
import ExportApiTokensMenuItem from 'common/components/menu-items/ExportApiTokensMenuItem';
import { OrganizationRolesEnum } from 'common/enum/Organization.enum';
import EditProfileMenuItem from 'common/components/menu-items/EditProfileMenuItem';
import AdminDashboardMenuItem from 'common/components/menu-items/AdminDashboardMenuItem';
import MenuItemsHeader from 'common/components/menu-items/menu-items-header.component';
import ThemeSwitchListItem from 'common/components/theme/theme-switch-list-item.component';
import { useDrawer, useSession } from 'common/hooks';

import { useOrganizationPrivacyPolicy } from 'features/organization-privacy-policy/hooks/useGetOrganizationPrivacyPolicy';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import MenuDrawerHeader from './menu-drawer-header.component';
import { FeatureGuard } from 'features/feature-access/components/feature-guard.component';
import { FeaturesEnum } from 'features/feature-access/features.enum';
import { FeatureAccessEnum } from 'features/feature-access/enums/feature-access.enum';

const SettingsDrawer: React.FC = () => {
  const { currentOrganization, organizationProfile } = useSession();
  const isSuperAdmin = organizationProfile?.role === OrganizationRolesEnum.SUPER_ADMIN;
  const { closeDrawer } = useDrawer();

  const supportChannelUrl = currentOrganization()?.supportChannelUrl;
  const { data: privacyPolicy } = useOrganizationPrivacyPolicy();

  const superAdminSettings = (
    <FeatureGuard
      features={[
        { feature: FeaturesEnum.ORGANIZATION_CREATION, access: FeatureAccessEnum.WRITE },
        { feature: FeaturesEnum.ADMIN_DASHBOARD, access: FeatureAccessEnum.WRITE },
      ]}
    >
      <>
        <MenuItemsHeader label="Super Admin Settings">
          <FeatureGuard
            features={[
              {
                feature: FeaturesEnum.ORGANIZATION_CREATION,
                access: FeatureAccessEnum.WRITE,
              },
            ]}
          >
            <CreateOrganizationMenuItem />
          </FeatureGuard>
          <FeatureGuard
            features={[
              { feature: FeaturesEnum.ADMIN_DASHBOARD, access: FeatureAccessEnum.WRITE },
            ]}
          >
            <AdminDashboardMenuItem />
          </FeatureGuard>
          {isSuperAdmin && <ExportApiTokensMenuItem />}
        </MenuItemsHeader>
        <Divider />
      </>
    </FeatureGuard>
  );

  const userSettings = (
    <FeatureGuard
      features={[{ feature: FeaturesEnum.USER_SETTINGS, access: FeatureAccessEnum.WRITE }]}
    >
      <MenuItemsHeader label="User Settings">
        <FeatureGuard
          features={[{ feature: FeaturesEnum.OWN_PROFILE, access: FeatureAccessEnum.WRITE }]}
        >
          <EditProfileMenuItem />
        </FeatureGuard>
        <FeatureGuard
          features={[
            { feature: FeaturesEnum.MANAGE_NOTIFICATIONS, access: FeatureAccessEnum.WRITE },
          ]}
        >
          <ManageNotificationsMenuItem />
        </FeatureGuard>
      </MenuItemsHeader>
      <Divider />
    </FeatureGuard>
  );

  return (
    <>
      <MenuDrawerHeader label="Settings" onClick={() => closeDrawer(DrawerIdEnum.SETTINGS)} />
      <VerticalScrollableContainer disableGutters>
        <div onClick={() => closeDrawer(DrawerIdEnum.SETTINGS)}>
          <List>
            <AboutConnectXMenuItem />
            {supportChannelUrl && (
              <OrganizationLinkMenuItem
                Icon={ContactSupportOutlinedIcon}
                label="Support Channel"
                url={supportChannelUrl}
              />
            )}
            {privacyPolicy?.url && (
              <OrganizationLinkMenuItem
                Icon={InfoOutlinedIcon}
                label="Privacy policy statement"
                url={privacyPolicy?.url}
              />
            )}
          </List>
          <Divider />
          <ThemeSwitchListItem />
          {userSettings}
          <FeatureGuard
            features={[
              { feature: FeaturesEnum.ADMIN_SETTINGS, access: FeatureAccessEnum.WRITE },
            ]}
          >
            <>
              <MenuItemsHeader label="Admin Settings">
                <ManageOrganizationUsersMenuItem />
                <ManageOrganizationMenuItem />

                <FeatureGuard features={[{ feature: FeaturesEnum.MANAGE_STATIC_DATA }]}>
                  <ManageOrganizationStaticDataMenuItem />
                </FeatureGuard>
              </MenuItemsHeader>
              <Divider />
            </>
          </FeatureGuard>
          {superAdminSettings}
          <List>
            <LogoutMenuItem />
          </List>
        </div>
      </VerticalScrollableContainer>
    </>
  );
};

export default SettingsDrawer;
